import { isPurchaseLike } from "../../utils/loans/delayedPurchase";
import { financedClosingCostsEstimate } from "./FinancedCostsFields";
/**
 * sizerAmounts — THE loan-size build-up for the RTL and GUC sizers, and it is a
 * preview. ONE copy for both screens, so the two cannot show a loan differently.
 *
 * Spec §1.1: the backend is the only calculator. What lives here is the declared
 * client-side mirror — the build-up, recomputed as the user types so the screen
 * is live before anything has been priced. It is labelled an estimate, it is
 * never sent back, and the moment a response lands the priced figures
 * (`result.sizing`, or the stored normalized scenario) supersede it.
 *
 * Every rule below is pinned to its backend source so the two cannot drift:
 * `_normalize_rtl_scenario` in eastview-os `app/routers/pricing.py` and
 * `LoanBaseModel.total_loan_amount_for` in `app/models/pipeline.py`.
 *
 *   basis    — purchase: MIN(asIsValue, purchasePrice) per property, either one
 *              standing in when the other is blank; refi: asIsValue.
 *   advance  — sizerInitialRequestedLeverage × basis, UNLESS baseLoanAmount is
 *              set (an entered amount wins outright). Financed closing costs
 *              are PART of the advance (owner 2026-11-01, §12b): a leverage-
 *              derived advance is grossed up by them; an entered one is taken
 *              as already inclusive.
 *   holdback — Σ constructionBudget × financedBudgetPercent, per-property
 *              percent falling back to the loan-level one, defaulting to 100%.
 *              Counts toward the total only on products that carry construction
 *              money — Stabilized Bridge / DSCR price off the advance alone.
 *   total    — advance + holdback.
 *
 * A financed interest reserve is NOT mirrored: it also sits inside the advance,
 * but its dollars come from the priced coupon, so only `pricedAmounts` has it.
 *
 * The percent normalization (`>1 → /100`) is the backend's, kept verbatim: a
 * form that stores 0.9 and one that stores 90 must both mean ninety percent.
 */

const num = (v) => (v == null || v === "" ? 0 : Number(v) || 0);

const asFraction = (v) => {
  if (v == null || v === "") return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return n > 1 ? n / 100 : n;
};

/**
 * Products whose total loan amount carries NO construction holdback — the ones
 * `LEVERAGE_METRICS_BY_PRODUCT` measures without LTC / LTARV. A stray budget
 * typed before switching to one of these must not inflate the total.
 */
const NO_HOLDBACK_PRODUCTS = ["Stabilized Bridge", "DSCR"];
export const productCarriesHoldback = (product) => !NO_HOLDBACK_PRODUCTS.includes(product);

// Purchase for every purchase rule — a delayed purchase (cash-out + the
// `delayedPurchase` flag, or a legacy "Delayed Purchase" value) counts.
export const isPurchasePurpose = (loanPurpose, delayedPurchase) =>
  isPurchaseLike(loanPurpose, delayedPurchase);

/** Σ of the cost basis the leverage is applied to. */
export function costBasis(values) {
  const purchase = isPurchasePurpose(values?.loanPurpose, values?.delayedPurchase);
  return (values?.subjectProperties || []).filter(Boolean).reduce((sum, p) => {
    const ai = num(p.asIsValue);
    const pp = num(p.purchasePrice);
    return sum + (purchase ? Math.min(ai || pp, pp || ai) : ai);
  }, 0);
}

/** Σ of the construction budget across the scenario's properties. */
export function constructionBudget(values) {
  return (values?.subjectProperties || [])
    .filter(Boolean)
    .reduce((sum, p) => sum + num(p.constructionBudget), 0);
}

/** Σ of the financed share of that budget — the construction holdback. */
export function financedHoldback(values) {
  if (!productCarriesHoldback(values?.loanProductType)) return 0;
  return (values?.subjectProperties || []).filter(Boolean).reduce((sum, p) => {
    const budget = num(p.constructionBudget);
    const pct = asFraction(p.financedBudgetPercent ?? values?.financedBudgetPercent);
    return sum + budget * (pct == null ? 1 : pct);
  }, 0);
}

/**
 * The build-up: initial advance (incl. financed closing costs) → + holdback →
 * = total loan amount.
 *
 * `overridden` says the advance came from an explicit `baseLoanAmount` rather
 * than from leverage — which is what a reprice suggestion writes, and what the
 * GUC strip has to be able to say out loud. `base` is an alias of `advance`.
 */
export function estimateAmounts(values) {
  const basis = costBasis(values);
  const holdback = financedHoldback(values);
  const override = values?.baseLoanAmount;
  const overridden = override != null && override !== "" && Number(override) > 0;

  const closingCosts = financedClosingCostsEstimate(
    values?.subjectProperties,
    isPurchasePurpose(values?.loanPurpose, values?.delayedPurchase),
  );

  const leverage = asFraction(values?.sizerInitialRequestedLeverage);
  const fromLeverage = leverage && basis ? leverage * basis : 0;
  const advance = overridden
    ? num(override)
    : fromLeverage
      ? fromLeverage + closingCosts
      : 0;

  return {
    basis,
    budget: constructionBudget(values),
    advance,
    base: advance,
    holdback,
    closingCosts,
    reserve: 0,
    total: advance + holdback,
    overridden,
    leverage,
  };
}

/**
 * The same numbers, taken from a RESPONSE instead of from the form.
 *
 * `result.sizing` itemises the loan (§12b): base (incl. any financed reserve
 * and financed closing costs) + holdback = total. Authoritative when present.
 * Before that backend change, the amounts live only on the STORED (normalized)
 * scenario — `scenario` is that, when the caller has it.
 */
export function pricedAmounts(result, scenario) {
  if (!result) return null;
  const z = result.sizing;
  if (z && (z.baseLoanAmount != null || z.totalLoanAmount != null)) {
    const holdback = num(z.constructionHoldback);
    return {
      advance: num(z.baseLoanAmount),
      holdback,
      closingCosts: num(z.financedClosingCosts),
      reserve: num(z.capitalizedInterestReserve),
      total: num(z.totalLoanAmount) || num(z.baseLoanAmount) + holdback,
    };
  }
  const s = scenario || {};
  const total = s.totalLoanAmount;
  // The posted BODY (a stopgap between the response and the doc re-read) has
  // no derived total. In amount mode it does carry the entered advance, and
  // reading that alone dropped the holdback from the total — so without a
  // derived total there are no priced amounts, and the caller keeps the
  // estimate, which carries the holdback the same way in both sizing modes.
  if (total == null || total === "") return null;
  return {
    advance: num(s.baseLoanAmount),
    holdback: num(s.totalHoldback),
    closingCosts: 0,
    reserve: 0,
    total: num(total),
  };
}

/**
 * The amounts a screen shows: priced when the response (or the stored scenario)
 * has them, otherwise the estimate from the values that priced — or, before any
 * run, from the live form. The one decision both sizers make the same way.
 */
export function shownAmounts({ result, scenario, pricedValues, values }) {
  const priced = pricedAmounts(result, scenario);
  if (result && priced) {
    return { ...estimateAmounts(pricedValues || values), ...priced, state: "priced" };
  }
  return { ...estimateAmounts((result && pricedValues) || values), state: "live" };
}
